import { Store, Middleware, createStore, applyMiddleware, PreloadedState } from 'redux'

import {
  createDelete,
  createInsert,
  createSelect,
  createSetSliceData,
  createDeleteSlice,
  createInsertSlice,
} from './actions/textProcessorActions'
import {
  IProcessorState,
  ISlice,
  ITextProcessor,
  IProcessorFabric,
  PROCESSOR_API_SOURCE,
  TextOperation,
  SliceComparator,
  PASTE_SOURCE,
} from './constants'
import { diff } from './diff'
import { createFragment } from './range'
import createReducer from './reducers'
import { getSelection, getSlices, getText } from './selectors/textProcessorSelectors'

class TextProcessor implements ITextProcessor {
  store: Store<IProcessorState, TextOperation>

  constructor(store: Store<IProcessorState>) {
    this.store = store
  }

  getState() {
    return this.store.getState()
  }

  getText() {
    return getText(this.store.getState())
  }

  getSelection() {
    return getSelection(this.store.getState())
  }

  getSlices() {
    return getSlices(this.store.getState())
  }

  delete(start: number, length: number) {
    this.store.dispatch(createDelete(start, length, PROCESSOR_API_SOURCE))
  }

  insert(start: number, text: string, slices?: ISlice[], source = PROCESSOR_API_SOURCE) {
    this.store.dispatch(createInsert(start, text, slices, source))
  }

  select(start: number, end?: number) {
    this.store.dispatch(createSelect(start, end))
  }

  input(text: string, selectionIndex: number, slices?: Partial<ISlice>[]): void {
    const result = diff(this.getText(), text, selectionIndex)
    if (result) {
      if (result.length > 0) {
        this.delete(result.start, result.length)
      }
      if (result.text) {
        if (slices) {
          slices = slices.map((f) => createFragment(0, result.text.length, f))
        }
        this.insert(result.start, result.text, slices as ISlice[])
      }
    }
  }

  paste(text: string, slices: ISlice[] = []): void {
    const selection = this.getSelection()
    const start = selection.start
    if (selection.length) {
      this.delete(start, selection.length)
    }
    if (text) {
      this.insert(selection.start, text, slices, PASTE_SOURCE)
    }
  }

  setSliceData(slice: ISlice, data: unknown): void {
    this.store.dispatch(createSetSliceData(slice, data))
  }

  deleteSlice(slice: ISlice): void {
    this.store.dispatch(createDeleteSlice(slice))
  }

  insertSlice(slice: ISlice): void {
    this.store.dispatch(createInsertSlice(slice))
  }

  render() {
    this.store.dispatch({ type: 'render' })
  }
}

interface IProcessorFabricCreator {
  (middleware: Middleware[], slicesEqual?: SliceComparator): IProcessorFabric
}

export const createProcessorFabric: IProcessorFabricCreator = (middleware, slicesEqual) => {
  const createProcessor = (state?: IProcessorState) => {
    const store = createStore(
      createReducer(slicesEqual),
      state as PreloadedState<IProcessorState>,
      applyMiddleware(...middleware),
    )
    return new TextProcessor(store)
  }
  return createProcessor
}
